import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { AuthResponse, User } from '@kerbly/shared';
import { api, clearTokens, getAccessToken, getRefreshToken, setTokens } from './api';

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (input: RegisterInput) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneCountryCode?: string;
  password: string;
  role?: 'operator' | 'customer';
  operatorProfile?: {
    businessName: string;
    abn?: string;
    businessAddress: string;
    businessCountryCode: string;
    currencyCode: string;
    serviceRadiusKm: number;
    suburb: string;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    timeZoneId?: string;
  };
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchMe = useCallback(async (): Promise<void> => {
    try {
      const { data } = await api.get<User>('/users/me');
      setUser(data);
    } catch {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      if (getAccessToken()) {
        await fetchMe();
      }
      if (!cancelled) setIsLoading(false);
    }
    void init();
    return () => {
      cancelled = true;
    };
  }, [fetchMe]);

  const login = useCallback(async (email: string, password: string): Promise<User> => {
    const { data } = await api.post<AuthResponse>('/auth/login', { email, password });
    setTokens(data.tokens);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (input: RegisterInput): Promise<User> => {
    const { data } = await api.post<AuthResponse>('/auth/register', {
      ...input,
      role: input.role ?? 'operator',
    });
    setTokens(data.tokens);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    const refreshToken = getRefreshToken();
    // Best-effort: tell the API to revoke the refresh token. We swallow errors
    // so a flaky network can't trap the user in a signed-in state.
    try {
      await api.post('/auth/logout', refreshToken ? { refreshToken } : {});
    } catch {
      // ignore
    }
    clearTokens();
    setUser(null);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchMe();
  }, [fetchMe]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isLoading, login, register, logout, refresh }),
    [user, isLoading, login, register, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
